/**
 * Pure model of "continue to the next lesson": when a lesson plays to its end, a short countdown opens the next one,
 * unless the viewer said otherwise. No React, no DOM: every rule here is unit-tested in continueModel.test.ts.
 *
 * Phases of one opened lesson:
 * - `armed`: the lesson may continue into the next one when it ends (the start, and after the viewer presses play).
 * - `paused`: the viewer paused; an end that still arrives (a pause racing the last second) does not continue.
 * - `counting`: the lesson ended and the countdown to the next lesson runs.
 * - `stopped`: the viewer canceled the countdown, replayed or stepped back in; this lesson never continues by itself
 *   again (the "Next lesson" button still does, on a click).
 */
import { lessonMeta, type TutorialLesson } from './tutorialsModel';

export const COUNTDOWN_MS = 5000;

export type ContinuePhase = 'armed' | 'paused' | 'counting' | 'stopped';
export type ContinueEvent = 'play' | 'pause' | 'finished' | 'cancel' | 'replay';

/** The next phase after `event`; `hasNext` is false on the last lesson, which has nothing to continue into. */
export function nextPhase(phase: ContinuePhase, event: ContinueEvent, hasNext: boolean): ContinuePhase {
  if (phase === 'stopped') return 'stopped';
  switch (event) {
    case 'cancel':
    case 'replay':
      return 'stopped';
    case 'pause':
      return phase === 'counting' ? 'stopped' : 'paused';
    case 'play':
      return phase === 'paused' ? 'armed' : phase;
    case 'finished':
      return phase === 'armed' && hasNext ? 'counting' : phase;
  }
}

/** Whole seconds still to wait, rounded up (5, 4 … 1, then 0 when the next lesson opens). */
export function secondsLeft(remainingMs: number): number {
  return Number.isFinite(remainingMs) ? Math.max(0, Math.ceil(remainingMs / 1000)) : 0;
}

/** 0..1 of the countdown still left, for the ring. */
export function remainingShare(remainingMs: number, totalMs: number = COUNTDOWN_MS): number {
  if (!(totalMs > 0) || !Number.isFinite(remainingMs)) return 0;
  return Math.min(1, Math.max(0, remainingMs / totalMs));
}

/** "Next lesson in 5…", then "Starting the next lesson…" at zero. */
export function countdownLabel(seconds: number): string {
  return seconds > 0 ? `Next lesson in ${seconds}…` : 'Starting the next lesson…';
}

/** What the "Up next" card says under the title: steps and time, or that this is the last lesson. */
export function upNextMeta(next: Pick<TutorialLesson, 'steps' | 'totalMs'> | null): string {
  return next ? lessonMeta(next) : 'This is the last lesson.';
}
